import { computed, Injectable, signal } from '@angular/core';
import { QuestionData } from '@app/core';
import { ConfirmationScreenDetail } from '@app/core/models/confirmation.model';

export interface RequiredDocumentsState {
  selectedBtn: string | null;
  showConfirmation: boolean;
  showQuestion: boolean;
  confirmationData: ConfirmationScreenDetail | null;
  isNextButtonDisabled: boolean;
  questionHistory: QuestionData[];
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})
export class RequiredDocumentsStateService {
  private readonly STORAGE_KEY = 'required_documents_state';
  private readonly QUESTION_STORAGE_KEY = 'question_state';
  private readonly STATE_EXPIRY = 3600000; // 1 hour

  // Private signal - only this service can modify
  private readonly _confirmationData = signal<ConfirmationScreenDetail | null>(null);
  private readonly _selectedButton = signal<string | null>(null);
  private readonly _isNextButtonDisabled = signal<boolean>(false);
  private readonly _showQuestion = signal<boolean>(false);
  private readonly _showConfirmation = signal<boolean>(false);

  // Question State
  private readonly _selectedOptions = signal<Map<string, string[]>>(new Map());
  private readonly _answers = signal<Map<string, any>>(new Map());
  private readonly _completedQuestions = signal<Set<string>>(new Set());
  private readonly _questionHistory = signal<QuestionData[]>([]);

  // Public readonly signals - components can read
  readonly confirmationData = this._confirmationData.asReadonly();
  readonly selectedButton = this._selectedButton.asReadonly();
  readonly isNextButtonDisabled = this._isNextButtonDisabled.asReadonly();
  readonly showQuestion = this._showQuestion.asReadonly();
  readonly showConfirmation = this._showConfirmation.asReadonly();

  readonly selectedOptions = this._selectedOptions.asReadonly();
  readonly answers = this._answers.asReadonly();
  readonly completedQuestions = this._completedQuestions.asReadonly();
  readonly questionHistory = this._questionHistory.asReadonly();

  // Computed signals
  readonly isNextDisabled = computed(() =>
    !this.selectedButton() || this._isNextButtonDisabled()
  );
  readonly hasConfirmationData = computed(() => this._confirmationData() !== null);
  readonly hasSelectedButton = computed(() => this._selectedButton() !== null);
  // readonly hasShowQuestion = computed(() => this._showQuestion());
  // readonly hasShowConfirmation = computed(() => this._showConfirmation());
  readonly hasState = computed(() =>
    this._selectedButton() !== null ||
    this._showConfirmation() ||
    this._showQuestion()
  );

  readonly questionProgress = computed(() => {
    const total = this._selectedOptions().size;
    const completed = this._completedQuestions().size;
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  });

  readonly hasQuestionHistory = computed(() => this._questionHistory.length > 0)

  // ✅ Required Documents Methods
  setSelectedBtn(value: string | null): void {
    this._selectedButton.set(value);
    // this.saveState();
  }

  setShowConfirmation(value: boolean): void {
    this._showConfirmation.set(value);
    // this.saveState();
  }

  setShowQuestion(value: boolean): void {
    this._showQuestion.set(value);
    // this.saveState();
  }

  setConfirmationData(data: ConfirmationScreenDetail | null): void {
    this._confirmationData.set(data);
    // this.saveState();
  }

  setIsNextButtonDisabled(value: boolean): void {
    this._isNextButtonDisabled.set(value);
    // this.saveState();
  }

  setQuestionHistory(value: QuestionData[]): void {
    this._questionHistory.set(value)
  }
  

  // ✅ Get complete state snapshot
  getState(): RequiredDocumentsState {
    return {
      selectedBtn: this._selectedButton(),
      showConfirmation: this._showConfirmation(),
      showQuestion: this._showQuestion(),
      confirmationData: this._confirmationData(),
      isNextButtonDisabled: this._isNextButtonDisabled(),
      questionHistory: this._questionHistory(),
      timestamp: Date.now()
    };
  }


  // ✅ Clear all state
  clearState(): void {
    this._selectedButton.set(null);
    this._showConfirmation.set(false);
    this._showQuestion.set(false);
    this._confirmationData.set(null);
    this._isNextButtonDisabled.set(false);
    this._questionHistory.set([]);
    
    sessionStorage.removeItem(this.STORAGE_KEY);
  }


}
